<script lang="ts">
	import { getDisplayName, getTokenLogo } from '$lib/perps';
	import type { Order } from '$lib/types/order';
	import GradientBorder from '$lib/util/GradientBorder.svelte';
	import { getMarketCurrentMarketPrice } from '$lib/util/marketHelper';
	import { getNumBackInUnits, addDollarSign, getDate, getTime } from '$lib/util/util';
	import BigNumber from 'bignumber.js';
	import { getDisplayAsset } from '../tradingView/util';
	import { getTakeProfitAndStopLossFromOrder, isEntirePositionTPSLOrder } from '../util';
	import { marketsConfig } from '$lib/config/markets';
	import { LARGE_POSITIVE_INT } from '$lib/web3/contants';
	import { optimism } from '@wagmi/core/chains';

	export let ord: Order;

	const isPairOrder = ord.priceA == null && ord.priceB == null;
	const isLong = isPairOrder ? new BigNumber(ord.size).lt(0) : new BigNumber(ord.size).gt(0);
	const { takeProfit, stopLoss } = getTakeProfitAndStopLossFromOrder(ord, isLong);

	const txLink =
		ord.orderType === 'Advanced'
			? `${optimism.blockExplorers?.default.url}/tx/${ord.latestTxHash}`
			: `${optimism.blockExplorers?.default.url}/tx/${ord.initiationTxHash}`;

	const getOrderStatus = () => {
		if (ord.status == 'FirstExecuted') {
			if (new BigNumber(ord.priceB).gte(new BigNumber(LARGE_POSITIVE_INT))) {
				if (new BigNumber(ord.size).gt(0)) {
					return 'Stop Executed';
				}
				return 'Limit Executed';
			}
			if (new BigNumber(ord.size).gt(0)) {
				return 'Limit Executed';
			}
			return 'Stop Executed';
		}
		if (ord.status == 'Cancelled' && ord.totalFee) {
			return 'TP/SL Cancelled';
		}
		if (ord.status == 'Completed' && ord.pairTotalFee && ord.pairExecutionType == 'TakeProfit') {
			return 'TP Executed';
		}
		if (ord.status == 'Completed' && ord.pairTotalFee && ord.pairExecutionType == 'StopLoss') {
			return 'SL Executed';
		}
		return ord.status;
	};
</script>

<!-- Desktop View -->
<a href={txLink} target="blank">
	<div
		class="hidden sm:grid grid-cols-7 border-t border-t-fx-grey-600 py-4 items-center px-2 hover:bg-fx-grey-900 active:bg-fx-grey-700 cursor-pointer"
	>
		<div class="flex flex-col gap-1 items-start justify-start">
			<div class="text-fx-grey-100 text-xs font-medium text-center">{getDate(ord.timestamp)}</div>
			<div class="text-fx-grey-300 text-xs font-medium text-center">{getTime(ord.timestamp)}</div>
		</div>
		<div class="flex gap-2">
			<img
				loading="lazy"
				src={getTokenLogo(ord.marketIdentifier)}
				alt="logo"
				class="w-5 h-5 rounded-full"
			/>
			<div class="text-fx-grey-100 text-sm rounded-lg font-medium">
				{getDisplayName(ord.marketIdentifier)}
			</div>
		</div>
		<div class="flex">
			<GradientBorder
				gradient="bg-fx-gradient-6"
				paddingClass="p-px"
				borderRadius="rounded-lg"
				outerClassList="w-fit my-auto mx-auto h-fit"
				innerClassList="/''bg-fx-black"
			>
				{#if new BigNumber(ord.size).gt(0)}
					<div
						class="bg-fx-gradient-8 px-4 py-1 text-fx-gradient-23 text-xs font-medium rounded-lg"
					>
						Long
					</div>
				{:else}
					<div
						class="bg-fx-gradient-8 px-4 py-1 text-fx-gradient-22 text-xs font-medium rounded-lg"
					>
						Short
					</div>
				{/if}
			</GradientBorder>
		</div>

		<div class="flex flex-col gap-1 items-start justify-center mx-auto">
			<div class="flex flex-col gap-1 items-start justify-center mx-auto">
				<div class="text-fx-grey-100 text-xs font-medium text-center">
					{isEntirePositionTPSLOrder(ord)
						? 'Entire Position'
						: getNumBackInUnits(ord.size, 18, 4) + ' ' + getDisplayAsset(ord?.asset)}
				</div>
			</div>
		</div>
		<div class="text-fx-grey-100 text-xs font-medium text-center">
			{#if ord.orderType == 'Advanced'}
				{#if ord.priceA && ord.priceB}
					{#if new BigNumber(ord.priceB).gte(new BigNumber(LARGE_POSITIVE_INT))}
						{#if new BigNumber(ord.size).gt(0)}
							Stop
						{:else}
							Limit
						{/if}
						≥ {addDollarSign(
							getNumBackInUnits(ord.priceA, 18, marketsConfig[ord.asset].showDecimals),
							marketsConfig[ord.asset].showDecimals
						)}
					{:else}
						{#if new BigNumber(ord.size).gt(0)}
							Limit
						{:else}
							Stop
						{/if}
						≤ {addDollarSign(
							getNumBackInUnits(ord.priceB, 18, marketsConfig[ord.asset].showDecimals),
							marketsConfig[ord.asset].showDecimals
						)}
					{/if}
				{/if}

				<div class={ord.priceA && ord.priceB ? 'text-fx-grey-300' : ''}>
					{#if new BigNumber(takeProfit).gt(0)}
						TP:{addDollarSign(
							getNumBackInUnits(takeProfit, 18, marketsConfig[ord.asset].showDecimals),
							marketsConfig[ord.asset].showDecimals
						)}
					{/if}
					{#if new BigNumber(takeProfit).gt(0) && new BigNumber(stopLoss).gt(0)}
						/
					{/if}
					{#if new BigNumber(stopLoss).gt(0)}
						SL:{new BigNumber(stopLoss).gte(1e24)
							? '$' +
							  Number.parseFloat(
									getNumBackInUnits(stopLoss, 18, marketsConfig[ord.asset].showDecimals)
							  ).toExponential(2)
							: addDollarSign(
									getNumBackInUnits(stopLoss, 18, marketsConfig[ord.asset].showDecimals),
									marketsConfig[ord.asset].showDecimals
							  )}
					{/if}
				</div>
			{:else}
				{ord.orderType}
				{#if ord.orderType == 'Limit'}
					@ {addDollarSign(getNumBackInUnits(ord.triggerPrice, 18, 3))} -
					{addDollarSign(getNumBackInUnits(ord.limitPrice, 18, 3))}
				{/if}
			{/if}
		</div>
		<div class="text-fx-grey-100 text-xs font-medium text-center">
			{getOrderStatus()}
		</div>
		{#if ord}
			<div
				class="text-fx-yellow text-xs font-medium text-right my-auto flex items-end gap-1 justify-end whitespace-nowrap"
			>
				{#if ord.orderType == 'Limit' || ord.orderType == 'Advanced'}
					{ord.totalFee
						? addDollarSign(getNumBackInUnits(ord.totalFee, 18, 4))
						: ord.pairTotalFee
						? addDollarSign(getNumBackInUnits(ord.pairTotalFee, 18, 4))
						: '-'}
				{:else}
					{addDollarSign(
						getNumBackInUnits(
							new BigNumber(ord.commitDeposit)
								.plus(new BigNumber(ord.keeperDeposit))
								.plus(new BigNumber(ord.tradeFee))
								.toString(),
							18,
							4
						)
					)}
				{/if}
				<img loading="lazy" src={'link-grey.png'} alt="etherscan" class="cursor-pointer w-5 h-5" />
			</div>
		{/if}
	</div>

	<!-- Mobile View -->
	<div class="sm:hidden block mb-1 p-1">
		<GradientBorder
			gradient="bg-fx-gradient-6"
			paddingClass="p-px"
			borderRadius="rounded-xl "
			outerClassList=" w-full h-full"
			innerClassList="flex justify-center items-center bg-fx-black w-full h-full"
		>
			<div class="bg-fx-gradient-8 w-full h-gull rounded-xl p-4 flex-col">
				<div class="flex justify-between mb-1">
					<div class="flex-col items-start mb-2">
						<div class="flex gap-2">
							<img
								loading="lazy"
								src={getTokenLogo(ord.marketIdentifier)}
								alt="logo"
								class="w-5 h-5 rounded-full"
							/>
							<div class="text-fx-grey-100 text-sm rounded-lg font-medium">
								{getDisplayName(ord.marketIdentifier)}
							</div>
							<GradientBorder
								gradient="bg-fx-gradient-6"
								paddingClass="p-px"
								borderRadius="rounded-lg "
								outerClassList="w-fit h-fit"
								innerClassList="/''bg-fx-black"
							>
								{#if new BigNumber(ord.size).gt(0)}
									<div
										class="bg-fx-gradient-8 px-4 py-1 text-fx-gradient-23 text-xs font-medium rounded-lg"
									>
										Long
									</div>
								{:else}
									<div
										class="bg-fx-gradient-8 px-4 py-1 text-fx-gradient-22 text-xs font-medium rounded-lg"
									>
										Short
									</div>
								{/if}
							</GradientBorder>
						</div>
					</div>
					<div class="flex flex-col items-start">
						<div class="flex gap-2">
							<div>
								<div class="text-fx-grey-100 text-xs font-medium text-right">
									{getNumBackInUnits(ord.size, 18, 4)}
									{ord.asset}
								</div>
								<div class="text-fx-grey-300 text-xs font-medium text-right">
									(${getNumBackInUnits(
										new BigNumber(ord.size)
											.multipliedBy(getMarketCurrentMarketPrice(ord.marketIdentifier))
											.div(1e18)
											.abs()
											.toString(),
										18,
										2
									)})
								</div>
							</div>
						</div>
					</div>
				</div>

				<div class="grid grid-cols-3 mt-2">
					<div class="flex-col">
						<div class="text-xs text-fx-grey-400">Execution Price</div>
						<div class="text-sm text-fx-grey-200 font-medium">
							{#if ord.orderType == 'Limit'}
								{addDollarSign(getNumBackInUnits(ord.triggerPrice, 18, 3))} -
								{addDollarSign(getNumBackInUnits(ord.limitPrice, 18, 3))}
							{:else}
								{addDollarSign(
									getNumBackInUnits(getMarketCurrentMarketPrice(ord.marketIdentifier), 18, 3)
								)}
							{/if}
						</div>
					</div>

					<div class="flex-col text-center">
						<div class="text-xs text-fx-grey-400 text-center">Status</div>
						<div class="text-sm text-fx-grey-200 font-medium text-center">
							{getOrderStatus()}
						</div>
					</div>
					<div class="flex-col text-right">
						<div class="text-xs text-fx-grey-400 text-end">Type</div>
						<div class="text-sm text-fx-grey-200 font-medium text-end">
							{ord.orderType} Order
						</div>
					</div>

					{#if ord}
						<div class="text-fx-yellow text-xs font-medium text-center my-auto">
							{#if ord.orderType == 'Limit'}
								{ord.totalFee ? addDollarSign(getNumBackInUnits(ord.totalFee, 18, 4)) : '-'}
							{:else}
								{addDollarSign(
									getNumBackInUnits(
										new BigNumber(ord.commitDeposit)
											.plus(new BigNumber(ord.keeperDeposit))
											.plus(new BigNumber(ord.tradeFee))
											.toString(),
										18,
										4
									)
								)}
							{/if}
						</div>
					{/if}
				</div>
			</div>
		</GradientBorder>
	</div>
</a>
